import { debounce, isEmpty, isString } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, Box, Typography } from "@mui/material";
import HighlightBorderTextField, {
    HighlightBorderTextFieldProps
} from "components/highlightBorderTextField";
import { overwriteICD10TypeName } from "dto/encounter.dto";
import { useEncounter } from "pages/encounter/encounter.provider";
import impressService from "services/impress";
// import useUmami from "hooks/useUmami";

export type CoderSectionProps = {
    name: overwriteICD10TypeName;

    index?: number;
    icdIndex?: number;
} & HighlightBorderTextFieldProps;

const ICDField = (props: CoderSectionProps) => {
    const { name, index = 0, icdIndex = 0, ...textFieldProps } = props;

    const [options, setOptions] = useState<any[]>([]);

    // const { track } = useUmami();

    const search = useCallback(async (keyword: string) => {
        const { data } = await impressService.searchICD10(keyword);
        setOptions(
            data.map((i) => ({
                code: i.code,
                term: i.term
            }))
        );
        // track("search-icd10", { keyword, result: data });
    }, []);

    const handleDebounceSearch = useMemo(() => {
        return debounce(search, 300);
    }, [search]);

    const { encounterForm } = useEncounter();

    const { control } = encounterForm;

    return (
        <Controller
            control={control}
            name={`${name}.${index}.icd.${icdIndex}` as const}
            render={({ field }) => {
                return (
                    <>
                        <Autocomplete
                            freeSolo
                            value={field?.value?.code ?? null}
                            options={options}
                            onInputChange={(_, value, reason) => {
                                if (reason === "input") {
                                    if (isEmpty(value)) {
                                        field.onChange(value);
                                    } else {
                                        field.onChange(value);
                                        handleDebounceSearch(value);
                                    }
                                }
                            }}
                            getOptionLabel={(option) => {
                                if (isString(option)) {
                                    return `${option}`;
                                } else {
                                    return `${option?.code ?? ""} ${
                                        option?.term ?? ""
                                    }`;
                                }
                            }}
                            onChange={(_, value) => {
                                field.onChange(value);
                            }}
                            filterOptions={(options) => options}
                            renderInput={(params) => {
                                return (
                                    <HighlightBorderTextField
                                        {...params}
                                        {...textFieldProps}
                                        value={field?.value}
                                        fullWidth
                                    />
                                );
                            }}
                        />
                        <Box px={1}>
                            <Typography variant="subtitle2">
                                {field?.value?.term ?? "-"}
                            </Typography>
                        </Box>
                    </>
                );
            }}
        />
    );
};

export default ICDField;
